import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import Modal from './Modal';
import ConfirmPopup from './ConfirmPopup';
import { logout, deleteAccount, getCurrentUsername, getUserSettings, updateProfileName } from '../services/api';

/**
 * User Settings — centered modal popup.
 *
 * Triggered by the gear icon in the left panel action row.
 * Shows username, magic word settings link, data source setup, help, and logout.
 *
 * Props:
 *  - show               : boolean
 *  - onClose            : () => void
 *  - openDataSourceSetup : () => void
 *  - onOpenHelp         : () => void
 *  - onOpenPrivacyPolicy : () => void
 *  - onOpenMagicWordSettings : () => void
 *  - onOpenFeedback     : () => void
 */
export default function UserSettingsModal({
  show,
  onClose,
  openDataSourceSetup,
  onOpenHelp,
  onOpenPrivacyPolicy,
  onOpenMagicWordSettings,
  onOpenFeedback,
}) {
  const [profile, setProfile] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameToast, setNameToast] = useState(null);
  const nameToastTimer = useRef(null);
  const nameInputRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Roomier than the default .btn for this full-width menu.
  const btnStyle = { width: '100%', padding: 'var(--space-md) var(--space-lg)' };
  const btnStackedStyle = { ...btnStyle, marginTop: 'var(--space-md)' };

  // Opens the product landing page — native iOS uses the Capacitor in-app
  // browser, web opens a new tab. Mirrors the auth flow in pages/Login.jsx.
  const openLanding = () => {
    const url = 'https://julynote.app/landing';
    if (Capacitor.isNativePlatform()) {
      Browser.open({ url, windowName: '_blank' });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteAccount(); // clears session + redirects on success
    } catch (e) {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteError(e.message || 'Failed to erase account');
    }
  };

  // ESC to close
  useEffect(() => {
    if (!show) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [show, onClose]);

  // Load display name + email when the modal opens
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setEditingName(false);
    getUserSettings()
      .then((data) => {
        if (!cancelled) {
          setProfile(data);
          setNameInput(data?.name || '');
        }
      })
      .catch(() => { /* ignore — fall back to username */ });
    return () => { cancelled = true; };
  }, [show]);

  function showNameToast(msg) {
    setNameToast(msg);
    clearTimeout(nameToastTimer.current);
    nameToastTimer.current = setTimeout(() => setNameToast(null), 2400);
  }

  function startEditName() {
    setNameInput(profile?.name || '');
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 50);
  }

  async function handleNameBlur() {
    const prev = profile?.name || '';
    if (nameInput === prev) {
      setEditingName(false);
      return;
    }
    if (savingName) return;
    setSavingName(true);
    try {
      const result = await updateProfileName(nameInput);
      setProfile(p => ({ ...p, name: result.name }));
      showNameToast(result.name ? `Saved — "${result.name}"` : 'Name cleared');
    } catch {
      showNameToast('Could not save');
      setNameInput(prev);
    } finally {
      setSavingName(false);
      setEditingName(false);
    }
  }

  // Email-registered users have an empty `name`, so the top line would fall
  // back to the email and then the line below would render it again.  Only
  // show the email as a secondary line when it isn't already the display name.
  const displayName = profile?.name || profile?.email || getCurrentUsername() || '—';
  const showEmail = Boolean(profile?.email && profile.email !== displayName);

  return (
    <>
    <Modal
      show={show}
      title="Settings"
      onClose={onClose}
      modalClass="modal-fixed"
    >
      {/* User info */}
      <div style={{ marginBottom: 'var(--space-md)' }}>
        <div className="v2-section-label">User</div>

        {editingName ? (
          <div style={{ marginTop: 'var(--space-xs)' }}>
            <input
              ref={nameInputRef}
              className="ds-input"
              type="text"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              placeholder="Your name"
              maxLength={100}
              onBlur={handleNameBlur}
              onKeyDown={e => {
                if (e.key === 'Enter') nameInputRef.current?.blur();
                if (e.key === 'Escape') {
                  setNameInput(profile?.name || '');
                  setEditingName(false);
                }
              }}
              style={{ width: '100%' }}
            />
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink)' }}>{displayName}</div>
              <button
                className="btn"
                onClick={startEditName}
                title="Edit name"
                aria-label="Edit name"
                style={{ padding: '2px', lineHeight: 1, background: 'transparent', border: 'none', color: 'var(--ink-muted)' }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                  style={{ width: '0.75rem', height: '0.75rem', display: 'block' }}>
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </div>
            {showEmail && (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>{profile.email}</div>
            )}
          </div>
        )}

        {nameToast && (
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--success-text)', marginTop: 'var(--space-xs)' }}>
            {nameToast}
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--rule)', margin: '0 0 var(--space-md) 0' }} />

      {/* Usage related */}
      <div style={{ marginBottom: 'var(--space-md)' }}>
        <button
          className="btn"
          onClick={() => {
            if (onOpenMagicWordSettings) onOpenMagicWordSettings();
          }}
          style={btnStyle}
        >
          Magic Word
        </button>
        <button
          className="btn"
          onClick={() => {
            onClose();
            openDataSourceSetup();
          }}
          style={btnStackedStyle}
        >
          Data Source
        </button>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--rule)', margin: '0 0 var(--space-md) 0' }} />

      {/* Help & privacy */}
      <div style={{ marginBottom: 'var(--space-md)' }}>
        <button
          className="btn"
          onClick={() => {
            onClose();
            if (onOpenHelp) onOpenHelp();
          }}
          style={btnStyle}
        >
          Help
        </button>
        <button
          className="btn"
          onClick={() => {
            onClose();
            if (onOpenFeedback) onOpenFeedback();
          }}
          style={btnStackedStyle}
        >
          Feedback
        </button>
        <button
          className="btn"
          onClick={() => {
            onClose();
            if (onOpenPrivacyPolicy) onOpenPrivacyPolicy();
          }}
          style={btnStackedStyle}
        >
          Privacy
        </button>
        <button
          className="btn"
          onClick={openLanding}
          style={btnStackedStyle}
        >
          Learn more about JulyNote
        </button>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--rule)', margin: '0 0 var(--space-md) 0' }} />

      {/* Logout & delete */}
      <div>
        <button
          className="btn"
          style={btnStyle}
          onClick={logout}
        >
          <span>🚪</span>
          <span>Logout</span>
        </button>
        <button
          className="btn btn-danger"
          onClick={() => setShowDeleteConfirm(true)}
          style={btnStackedStyle}
        >
          Erase account
        </button>
        {deleteError && (
          <div style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--danger)',
            marginTop: 'var(--space-sm)',
          }}>
            {deleteError}
          </div>
        )}
      </div>
    </Modal>

    <ConfirmPopup
      show={showDeleteConfirm}
      title="Erase account?"
      message={`This erases your account and data-source settings.\n\nYour notes and files stay on your data source (GitHub/Gitee).\n\nCreate a new account anytime to continue.`}
      confirmLabel={deleting ? 'Erasing…' : 'Erase account'}
      confirmDisabled={deleting}
      danger
      onConfirm={handleDeleteAccount}
      onClose={() => setShowDeleteConfirm(false)}
    />
    </>
  );
}
